import { useQuery } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { useNavigate } from 'react-router-dom';

import { ConfirmDialog } from '../../components/ui/ConfirmDialog';
import { ErrorState } from '../../components/ui/Feedback';
import { FieldError } from '../../components/ui/FieldError';
import { DetailSkeleton } from '../../components/ui/Skeleton';
import { ApiError } from '../../lib/api-client';
import { getSession, isRememberedSession, logout, replaceSession } from '../../lib/auth';
import { confirmUpdate } from '../../lib/confirm-service';
import { formatDate } from '../../lib/datetime';
import { useHospitalTitle } from '../../lib/hospital-brand';
import { changeOwnPassword, endSession, fetchPatientProfile } from './auth.api';

const valueOrDash = (value: string | null | undefined) => value?.trim() || '-';

export const PatientPortalPage = () => {
  const navigate = useNavigate();
  const hospitalTitle = useHospitalTitle();
  const session = getSession();
  const profile = useQuery({ queryKey: ['auth', 'patient-profile'], queryFn: fetchPatientProfile });
  const [passwords, setPasswords] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [passwordState, setPasswordState] = useState<{ pending: boolean; error: string | null; success: string | null }>({ pending: false, error: null, success: null });
  const [showPasswordErrors, setShowPasswordErrors] = useState(false);
  const [signOutDialog, setSignOutDialog] = useState(false);
  const [signingOut, setSigningOut] = useState(false);
  const passwordFieldErrors = showPasswordErrors ? (() => {
    const errors: Record<string, string> = {};
    if (!passwords.currentPassword.trim()) errors.currentPassword = 'Current password is required.';
    if (passwords.newPassword.length < 8) errors.newPassword = 'New password must be at least 8 characters.';
    else if (passwords.confirmPassword !== passwords.newPassword) errors.confirmPassword = 'New password and confirmation do not match.';
    return errors;
  })() : {};

  const signOut = async () => {
    setSigningOut(true);
    try { await endSession(); } catch { /* Local logout still protects this browser. */ }
    logout();
    void navigate('/login', { replace: true });
  };

  const submitPassword = async (event: FormEvent) => {
    event.preventDefault();
    setShowPasswordErrors(true);
    setPasswordState({ pending: false, error: null, success: null });
    if (Object.keys(passwordFieldErrors).length > 0) return;
    const confirmed = await confirmUpdate('your account password');
    if (!confirmed) return;
    setPasswordState({ pending: true, error: null, success: null });
    try {
      replaceSession(await changeOwnPassword({ currentPassword: passwords.currentPassword, newPassword: passwords.newPassword, remember: isRememberedSession() }));
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setPasswordState({ pending: false, error: null, success: 'Password changed successfully.' });
    } catch (error) {
      setPasswordState({ pending: false, error: error instanceof ApiError ? error.message : 'Password could not be changed.', success: null });
    }
  };

  return (
    <div className="patient-portal">
      <header className="patient-portal-header">
        <div><span className="brand-mark">+</span><div><strong>{hospitalTitle}</strong><small>Patient portal</small></div></div>
        <div><span>Signed in as {session?.user.name}</span><button className="button" type="button" onClick={() => setSignOutDialog(true)}>Sign out</button></div>
      </header>
      <main className="patient-portal-main">
        <div className="page-heading"><div><p className="eyebrow">My hospital record</p><h1>Welcome, {session?.user.name}</h1><p>Your patient profile and hospital workspace are available from the same account.</p></div></div>
        {session?.user.mustChangePassword ? <div className="inline-alert">For security, change the temporary password assigned by administration.</div> : null}
        {profile.isLoading ? <DetailSkeleton label="Loading your profile" /> : null}
        {profile.isError ? <ErrorState title="Unable to load your patient profile" message="Please try again or contact hospital administration." action={{ label: 'Try again', onClick: () => void profile.refetch() }} /> : null}
        {profile.data ? (
          <section className="patient-portal-card">
            <div className="patient-portal-card-title"><div><p>Patient</p><h2>{profile.data.name}</h2></div><strong>UHID {profile.data.uhid}</strong></div>
            <dl className="patient-portal-details">
              <div><dt>Phone</dt><dd>{valueOrDash(profile.data.phone)}</dd></div>
              <div><dt>Date of birth</dt><dd>{profile.data.dateOfBirth ? formatDate(profile.data.dateOfBirth) : '-'}</dd></div>
              <div><dt>Gender</dt><dd>{valueOrDash(profile.data.gender)}</dd></div>
              <div><dt>Blood group</dt><dd>{valueOrDash(profile.data.bloodGroup)}</dd></div>
              <div><dt>Guardian</dt><dd>{valueOrDash(profile.data.guardianName)}</dd></div>
              <div><dt>Guardian phone</dt><dd>{valueOrDash(profile.data.guardianPhone)}</dd></div>
              <div className="wide"><dt>Address</dt><dd>{[profile.data.address, profile.data.district, profile.data.state, profile.data.postalCode].filter(Boolean).join(', ') || '-'}</dd></div>
            </dl>
          </section>
        ) : null}
        <section className="patient-portal-card patient-password-card">
          <div><p className="eyebrow">Account security</p><h2>Change password</h2><p>Use at least 8 characters and do not share your password.</p></div>
          <form onSubmit={(event) => void submitPassword(event)} noValidate>
            <label>Current password<input type="password" value={passwords.currentPassword} autoComplete="current-password" onChange={(event) => setPasswords((current) => ({ ...current, currentPassword: event.target.value }))} aria-invalid={Boolean(passwordFieldErrors.currentPassword)} /><FieldError message={passwordFieldErrors.currentPassword} /></label>
            <label>New password<input type="password" value={passwords.newPassword} autoComplete="new-password" onChange={(event) => setPasswords((current) => ({ ...current, newPassword: event.target.value }))} aria-invalid={Boolean(passwordFieldErrors.newPassword)} /><FieldError message={passwordFieldErrors.newPassword} /></label>
            <label>Confirm new password<input type="password" value={passwords.confirmPassword} autoComplete="new-password" onChange={(event) => setPasswords((current) => ({ ...current, confirmPassword: event.target.value }))} aria-invalid={Boolean(passwordFieldErrors.confirmPassword)} /><FieldError message={passwordFieldErrors.confirmPassword} /></label>
            {passwordState.error ? <div className="inline-alert" role="alert">{passwordState.error}</div> : null}
            {passwordState.success ? <div className="settings-success" role="status">{passwordState.success}</div> : null}
            <button className="button button-primary" type="submit" disabled={passwordState.pending}>{passwordState.pending ? 'Changing...' : 'Change password'}</button>
          </form>
        </section>
      </main>
      <ConfirmDialog
        open={signOutDialog}
        title="Sign out of your account?"
        description="You'll need to sign in again to view your patient record."
        confirmLabel="Sign out"
        pendingLabel="Signing out..."
        tone="danger"
        pending={signingOut}
        onCancel={() => setSignOutDialog(false)}
        onConfirm={() => void signOut()}
      />
    </div>
  );
};
